// SalesPersonMatrixPage.jsx
import React, { useEffect, useState } from 'react';
import HeadingCp from '../components/HeadingCp';
import CurrencyFormat from '../components/CurrencyFormat';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

const SalesPersonMatrixPage = () => {
  const apiUrl = process.env.REACT_APP_API_URL;
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [salesMatrix, setSalesMatrix] = useState([]);
  const [selectedSalesPerson, setSelectedSalesPerson] = useState(null);

  const [selectedSalesCode, setSelectedSalesCode] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

useEffect(() => {
  fetchSalesMatrix();
}, []);

  const fetchSalesMatrix = async () => {
    setLoading(true);

    try {
      const { data } = await axios.get(`${apiUrl}/report_crud.php`, {
        params: {
          getSalesPersonMatrix: true,
          sales_code: selectedSalesCode,
          start_date: startDate,
          end_date: endDate,
        },
      });
      setSalesMatrix(data?.sales_matrix ?? []);
    } catch (error) {
      if (error.response?.status === 401) {
        navigate('/login');
      } else if (error.response?.status === 403) {
        navigate('/');
      } else {
        toast.error('Failed to load sales matrix.', {
          position: 'top-center',
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const totals = salesMatrix.reduce(
    (acc, person) => {
      acc.proposal_count += Number(person.proposal_count || 0);
      acc.proposal_sum += Number(person.proposal_sum || 0);
      acc.awarded_count += Number(person.awarded_count || 0);
      acc.awarded_sum += Number(person.awarded_sum || 0);
      return acc;
    },
    {
      proposal_count: 0,
      proposal_sum: 0,
      awarded_count: 0,
      awarded_sum: 0,
    }
  );

  const totalCloseRate =
    totals.proposal_count > 0
      ? ((totals.awarded_count / totals.proposal_count) * 100).toFixed(2)
      : 0;

  const handleReset = async () => {
    setSelectedSalesCode('');
    setStartDate('');
    setEndDate('');

    setLoading(true);

    try {
      const { data } = await axios.get(`${apiUrl}/report_crud.php`, {
        params: {
          getSalesPersonMatrix: true,
        },
      });

      setSalesMatrix(data?.sales_matrix ?? []);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-7xl sm:px-4 lg:px-6 py-10">
      <div className="sm:flex sm:items-center mb-8">
        <div className="sm:flex-auto">
          <HeadingCp label="Sales Person Matrix" />
          {/* <p className="mt-2 text-sm text-gray-500">
            Salesperson is detected from initials inside the job number.
          </p> */}
        </div>

        <div className="mt-4 sm:mt-0 sm:ml-4">
          <button
            onClick={() => window.print()}
            className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 print:hidden"
          >
            Print
          </button>     
        </div>
      </div>

    <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-8 print:hidden">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Salesperson
        </label>
        <select
          value={selectedSalesCode}
          onChange={(e) => setSelectedSalesCode(e.target.value)}
          className="block w-full rounded-md border-gray-300 shadow-sm text-sm"
        >
          <option value="">All Salespeople</option>
          {salesMatrix.map((person) => (
            <option key={person.initials} value={person.initials}>
              {person.name} - {person.initials}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Start Date
        </label>
        <input
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          className="block w-full rounded-md border-gray-300 shadow-sm text-sm"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          End Date
        </label>
        <input
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          className="block w-full rounded-md border-gray-300 shadow-sm text-sm"
        />
      </div>

      <div className="flex items-end gap-2">
        <button
          onClick={fetchSalesMatrix}
          className="inline-flex justify-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700"
        >
          Search
        </button>

        <button
          onClick={handleReset}
          className="inline-flex justify-center rounded-md bg-orange-500 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-orange-600"
        >
          Reset
        </button>
      </div>
    </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 mb-10">
        <MetricCard
          label="Proposals Sent"
          value={`${totals.proposal_count}`}
          money={totals.proposal_sum}
        />

        <MetricCard
          label="Awarded Jobs"
          value={`${totals.awarded_count}`}
          money={totals.awarded_sum}
        />

        <MetricCard
          label="Close Rate"
          value={`${totalCloseRate}%`}
        />

        <MetricCard
          label="Average Awarded Job"
          money={
            totals.awarded_count > 0
              ? totals.awarded_sum / totals.awarded_count
              : 0
          }
        />
      </div>

      {loading && (
        <div className="p-4 bg-white shadow rounded-lg text-sm text-gray-500">
          Loading sales matrix...
        </div>
      )}

      {!loading && (
        <div className="bg-white shadow rounded-lg overflow-hidden mb-10">
          <table className="min-w-full divide-y divide-gray-300">
            <thead className="bg-gray-50">
              <tr>
                <Th>Sales</Th>
                <Th>Proposals Sent</Th>
                <Th>Proposal Sum</Th>
                <Th>Awarded Jobs</Th>
                <Th>Awarded Sum</Th>
                <Th>Close Rate</Th>
                <Th>Avg Proposal</Th>
                <Th>Avg Awarded</Th>
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-200">
              {salesMatrix.map((person) => (
                <tr
                  key={person.initials}
                  onClick={() => setSelectedSalesPerson(person)}
                  className="hover:bg-indigo-50 cursor-pointer"
                >
                  <Td strong>
                    <div>
                      <div>{person.name || 'Unknown Salesperson'}</div>
                      <div className="text-xs text-gray-400">{person.initials}</div>
                    </div>
                  </Td>

                  <Td>{person.proposal_count}</Td>
                  <Td>
                    <CurrencyFormat value={person.proposal_sum} />
                  </Td>
                  <Td>{person.awarded_count}</Td>
                  <Td>
                    <CurrencyFormat value={person.awarded_sum} />
                  </Td>
                  <Td>{person.close_rate}%</Td>
                  <Td>
                    <CurrencyFormat value={person.avg_proposal} />
                  </Td>
                  <Td>
                    <CurrencyFormat value={person.avg_awarded} />
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selectedSalesPerson && (
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">
              Yearly Breakdown - {selectedSalesPerson.initials}
            </h2>

            <button
              onClick={() => setSelectedSalesPerson(null)}
              className="text-sm text-gray-500 hover:text-gray-700 print:hidden"
            >
              Close
            </button>
          </div>

          <table className="min-w-full divide-y divide-gray-300">
            <thead>
              <tr>
                <Th>Year</Th>
                <Th>Proposals</Th>
                <Th>Proposal Sum</Th>
                <Th>Awarded Jobs</Th>
                <Th>Awarded Sum</Th>
                <Th>Close Rate</Th>
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-200">
              {(selectedSalesPerson.years || []).map((year) => {
                const closeRate =
                  year.proposal_count > 0
                    ? (
                        (year.awarded_count / year.proposal_count) *
                        100
                      ).toFixed(2)
                    : 0;

                return (
                  <tr key={year.year}>
                    <Td strong>{year.year}</Td>
                    <Td>{year.proposal_count}</Td>
                    <Td>
                      <CurrencyFormat value={year.proposal_sum} />
                    </Td>
                    <Td>{year.awarded_count}</Td>
                    <Td>
                      <CurrencyFormat value={year.awarded_sum} />
                    </Td>
                    <Td>{closeRate}%</Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const MetricCard = ({ label, value, money }) => (
  <div className="p-4 bg-white shadow rounded-lg">
    <p className="text-sm font-medium text-gray-500">{label}</p>
    <p className="mt-1 text-2xl font-semibold text-gray-900">
      <span className='pr-1 text-green-700'>{value && value}</span>
      {money !== undefined && <CurrencyFormat value={money} />}
    </p>
  </div>
);

const Th = ({ children }) => (
  <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
    {children}
  </th>
);

const Td = ({ children, strong }) => (
  <td
    className={`whitespace-nowrap px-3 py-4 text-sm ${
      strong ? 'font-semibold text-gray-900' : 'text-gray-500'
    }`}
  >
    {children}
  </td>
);

export default SalesPersonMatrixPage;